// AIDEV-NOTE: Single source of truth for user display name resolution
// Display name shows FIRST NAME extracted from fullName, falling back to email

interface DisplayNameUser {
    id?: string;
    displayName?: string | null;
    fullName?: string | null;
    email?: string | null;
}

/**
 * Returns the user's first name (from fullName) or email as a display name.
 * Fallback chain: firstName -> email -> fallback
 */
export function getDisplayName(
    user: DisplayNameUser | null | undefined,
    fallback = "Unknown",
): string {
    if (!user) return fallback;
    const firstName = getFirstName(user.fullName);
    return firstName || user.email?.trim() || fallback;
}

/**
 * Returns the user's full name, falling back to email then fallback string.
 * Use this instead of getDisplayName when you need the complete name
 * (e.g., in emails, formal contexts).
 */
export function getFullDisplayName(
    user: DisplayNameUser | null | undefined,
    fallback = "Unknown",
): string {
    if (!user) return fallback;
    const displayName = user.displayName?.trim();
    if (displayName) return displayName;
    const trimmed = user.fullName?.trim();
    return trimmed || user.email?.trim() || fallback;
}

function normalizeNameForDisambiguation(
    fullName?: string | null,
): string | null {
    const trimmed = fullName?.trim();
    if (!trimmed) return null;
    return trimmed.replace(/\s+/g, " ").toLowerCase();
}

// AIDEV-NOTE: Server-side helper for practitioner-scoped client lists.
// If two clients share the same fullName, append email to both for UI disambiguation.
export function withDisambiguatedDisplayNames<
    T extends { id: string; fullName?: string | null; email?: string | null },
>(users: T[]): Array<T & { displayName: string }> {
    const nameCounts = new Map<string, number>();

    for (const user of users) {
        const key = normalizeNameForDisambiguation(user.fullName);
        if (!key) continue;
        nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
    }

    return users
        .map((user) => {
            const fullName = user.fullName?.trim() || null;
            const email = user.email?.trim() || null;
            const key = normalizeNameForDisambiguation(fullName);
            const hasDuplicateName = key
                ? (nameCounts.get(key) ?? 0) > 1
                : false;

            const displayName =
                hasDuplicateName && fullName && email
                    ? `${fullName} (${email})`
                    : fullName || email || "Unknown";

            return {
                ...user,
                displayName,
            };
        })
        .sort((a, b) =>
            a.displayName.localeCompare(b.displayName, undefined, {
                sensitivity: "base",
            }),
        );
}

// AIDEV-NOTE: Extract first name from fullName via simple space split
export function getFirstName(fullName?: string | null): string | null {
    const trimmed = fullName?.trim();
    if (!trimmed) return null;
    return trimmed.split(/\s+/)[0];
}

export function getInitials(user: {
    fullName?: string | null;
    email?: string | null;
}): string {
    const name = user.fullName?.trim();
    if (name) {
        const parts = name.split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    }
    // Fall back to email-based initials
    const email = user.email || "";
    const prefix = email.split("@")[0];
    if (prefix.includes(".")) {
        const [first, last] = prefix.split(".");
        if (first && last) return (first[0] + last[0]).toUpperCase();
    }
    return prefix.slice(0, 2).toUpperCase();
}
